<template>
  <div class="appointment-page">
    <!-- Global Loading Overlay - Only for initial page load -->
    <global-loading :isVisible="!hasLoaded" :message="$t('appointment.chooseSlotPage.calendar.loadingSlots')" />

    <v-container id="appointment" data-id="appointment" v-if="hasLoaded"
      :class="$vuetify.breakpoint.smAndUp ? 'centered-container' : ''">
      <fo-header :content="headerData" :config="clientPage?.config.header" :availableLanguages="availableLanguages"
        dataId="appointment-header" @changeLanguage="getClientPage($event)" />

      <v-row no-gutters class="container px-4 mx-4">
        <v-col :cols="$vuetify.breakpoint.smAndUp ? 6 : 12" :offset="$vuetify.breakpoint.smAndUp ? 3 : 0">
          <appointmentTitle :title="$t('appointment.chooseSlotPage.title')" :subtitle="originalSlot
            ? $t('appointment.chooseSlotPage.initialSlotSubtitle', {
              slot: originalSlot.formattedSlot,
            })
            : $t('appointment.chooseSlotPage.subtitle')
            " :subtitleFormat="originalSlot ? 'raised' : ''" />
        </v-col>

        <v-col :cols="$vuetify.breakpoint.smAndUp ? 10 : 12" :offset="$vuetify.breakpoint.smAndUp ? 1 : 0" class="mt-8">
          <appointment-calendar :selectedSlot="getDeliverySlot()" :deliveryDate="deliveryDate"
            :pickingDates="pickingDates" :availableSlots="slots" :isLoadingSlots="isLoadingSlots"
            @daySelected="handleDaySelected" @slotSelected="handleSlotSelected" @validateSlot="handleValidateSlot"
            @clearSlots="clearSlots" @monthChanged="handleMonthChanged" :language="language"
            :minDeliveryPromiseDate="minDeliveryPromiseDate" :unavailableDates="unavailableDates" />
        </v-col>
      </v-row>

      <portal to="footer">
        <fo-footer :config="clientPage?.config?.footer" dataId="appointment-footer" />
      </portal>
    </v-container>
  </div>
</template>

<script>
import foFooter from '@/components/footer/footer.vue'
import foHeader from '@/components/header/header.vue'
import GlobalLoading from '@/components/loading/GlobalLoading.vue'
import {
  DEFAULT_LANGUAGE,
  DeliveryClientPageType,
  MESSAGE_TIMEOUTS,
  REDIRECTION_DELAY,
  ROUTES,
} from '@/constants'
import { DateFormat, dateHelper, DEFAULT_CUSTOMER_TIMEZONE } from '@/helpers/dateHelper'
import { clientPageMixin } from '@/mixins'
import DeliveryService from '@/services/delivery/DeliveryService'
import { SnackBarType } from '@/store/types'
import { OperationUnit } from '@meltingpoint/lastmile-internationalization'
import { differenceInCalendarDays, intlFormat } from 'date-fns'
import mixins from 'vue-typed-mixins'
import appointmentCalendar from './appointmentCalendar.vue'
import appointmentTitle from './appointmentTitle.vue'
export default mixins(clientPageMixin).extend({
  name: 'mp-appointment',

  components: {
    foFooter,
    foHeader,
    GlobalLoading,
    appointmentTitle,
    appointmentCalendar,
  },
  data() {
    return {
      hasLoaded: false,
      slots: [],
      isLoadingSlots: false,
      originalSlot: null,
      selectedSlot: null,
      isDifferentFromOriginal: false,
      canUpdate: false,
      isConfirmationPage: false,
      selectedDay: null,
      errorMessage: null,
      pickingDates: null,
      unavailableDates: [],
      isLoadingUnavailableDates: false,
    }
  },
  async beforeMount() {
    try {
      await this.initializeComponent()
      await this.loadInitialData()
      this.setupComponent()

    } catch (error) {
      this.handleInitializationError(error)
    }
  },
  computed: {
    retailerName() {
      return this.clientPage?.retailerName
    },
    deliveryDate() {
      // check if the delivery date is valid for delivery
      if (this.delivery?.deliveryDate && this.isDateValidForDelivery(this.delivery?.deliveryDate)) {
        return this.delivery?.deliveryDate
      }
      return null
    },
    language() {
      const {
        query: { language },
      } = this.$route
      const urlLanguage = language?.toString()
      const pageLanguage = this.clientPage?.language
      return urlLanguage || pageLanguage || DEFAULT_LANGUAGE
    },
  },

  methods: {
    handleInitializationError(error) {
      this.$store.dispatch('setSnackbar', {
        message: this.$t('messages.error'),
        type: SnackBarType.ERROR,
        timeout: MESSAGE_TIMEOUTS.DEFAULT,
      })
    },

    async initializeComponent() {
      const {
        query: { language },
        params: { accessToken },
      } = this.$route

      const languageToUse = language?.toString()
      await this.getClientPage(languageToUse)

      if (!this.validateAccessToken(accessToken)) {
        return
      }

      this.setupInitialState()
    },

    validateAccessToken(accessToken) {
      if (!accessToken || typeof accessToken !== 'string' || accessToken.trim().length === 0) {
        this.$store.dispatch('setSnackbar', {
          message: this.$t('messages.noAccessToken'),
          type: SnackBarType.ERROR,
          timeout: MESSAGE_TIMEOUTS.ERROR,
        })
        this.$router.push({ name: ROUTES.NOT_FOUND })
        return false
      }
      return true
    },

    setupInitialState() {
      this.originalSlot = this.getOriginalSlot()

      if (
        this.originalSlot &&
        this.originalSlot.start &&
        this.isDateValidForDelivery(this.originalSlot.start)
      ) {
        this.selectedDay = this.originalSlot.start
      } else if (this.minDeliveryPromiseDate) {
        this.selectedDay = this.minDeliveryPromiseDate
      } else {
        this.selectedDay = null
      }

      if (this.selectedDay) {
        this.selectedSlot = this.originalSlot
      }
    },

    async loadInitialData() {
      const { accessToken } = this.$route.params
      this.checkModificationAvailability()

      const order =
        (await (await DeliveryService.get(accessToken, { type: 'APPOINTMENT' })).globalOrder) ||
        null
      if (
        order.picking?.interval &&
        Array.isArray(order.picking.interval) &&
        order.picking.interval.length > 0
      ) {
        this.pickingDates = order.picking.interval[0]
      } else {
        this.pickingDates = null
      }

      // Only load slots if user can update (canUpdate is true)
      if (this.canUpdate && this.isDateValidForDelivery(this.selectedDay)) {
        await this.loadInitialSlots(accessToken)
      }
    },

    setupComponent() {
      if (!this.clientPage || !this.delivery) {
        this.$router.push({ name: ROUTES.NOT_FOUND })
        return
      }
      this.hasLoaded = true
    },

    getOriginalSlot() {
      if (this.delivery && this.delivery.deliverySlot) {
        const { start, end } = this.delivery.deliverySlot
        const formattedSlot = this.getFormattedSlot(this.delivery.deliverySlot, true)
        return {
          start,
          end,
          formattedSlot,
        }
      }
      return null
    },

    getDeliverySlot() {
      return this.selectedSlot || null
    },
    getClientTimezone() {
      return this.delivery?.shipping?.timezone ?? DEFAULT_CUSTOMER_TIMEZONE
    },
    getFormattedSlot(slot, withDate = false) {
      const startTime = dateHelper.getDate(slot.start).toLocaleTimeString(this.language, {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: this.getClientTimezone(),
      })
      const endTime = dateHelper.getDate(slot.end).toLocaleTimeString(this.language, {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: this.getClientTimezone(),
      })
      if (withDate) {
        const isTheSameDay =
          dateHelper.getDate(slot.start).getDay() === dateHelper.getDate(slot.end).getDay()
        if (isTheSameDay) {
          const date = intlFormat(
            dateHelper.getDate(slot.start),
            {
              dateStyle: 'long',
            },
            {
              locale: this.language,
            },
          )
          return `${date} / ${startTime} - ${endTime}`
        } else {
          const startDate = dateHelper.format(slot.start, DateFormat.MEDIUM, this.language)
          const endDate = dateHelper.format(slot.end, DateFormat.MEDIUM, this.language)
          return `${startDate} / ${startTime} - ${endDate} / ${endTime}`
        }
      }
      return `${startTime} - ${endTime}`
    },

    checkModificationAvailability() {
      this.canUpdate = this.delivery?.canUpdate || false

      if (!this.canUpdate) {
        const { accessToken } = this.$route.params
        this.$router.push({
          name: DeliveryClientPageType.APPOINTMENT_CONFIRM,
          params: { accessToken, language: this.language },
        })
      }
      if (!this.delivery) {
        this.$router.push({ name: ROUTES.NOT_FOUND })
      }
    },

    async loadInitialSlots(accessToken) {
      try {
        let fromDate = null
        if (
          this.minDeliveryPromiseDate &&
          dateHelper.isSame(
            dateHelper.getDate(this.selectedDay),
            dateHelper.getDate(this.minDeliveryPromiseDate),
            OperationUnit.DAYS,
          )
        ) {
          fromDate = this.minDeliveryPromiseDate
        } else {
          fromDate = this.selectedDay.split('T')[0] + 'T00:00:00.000Z'
        }

        const toDate = dateHelper.endOf(dateHelper.getDate(this.selectedDay), 'day').toISOString()

        const response = await DeliveryService.getSlots(accessToken, {
          fromDate,
          toDate,
        })

        if (response && response.carrierSlots && response.carrierSlots.length > 0) {
          const rawSlots = response.carrierSlots[0].slots

          const mappedSlots = this.mapSlotsToExpectedFormat(rawSlots)
          this.slots = this.sortSlots(mappedSlots)
        } else {
          this.slots = []
        }
      } catch (error) {
        this.slots = []
        this.showError(error)
      }
    },

    async validateSlotWithoutBooking(slot) {
      const {
        params: { accessToken },
      } = this.$route
      try {
        const { data } = await DeliveryService.chooseSlot(accessToken, {
          slot: {
            start: slot.start,
            end: slot.end,
          },
        })

        return data
      } catch (error) {
        return { status: 'error', message: this.$t('messages.appointment.slotValidationFailed') }
      }
    },

    async handleDaySelected(selectedDay) {
      try {
        if (!this.isDateValidForDelivery(selectedDay)) {
          this.showError(
            this.$t('messages.appointment.dateNotAvailable', {
              minDate: this.minDeliveryPromiseDate
                ? intlFormat(
                  new Date(this.minDeliveryPromiseDate),
                  {
                    dateStyle: 'medium',
                  },
                  {
                    locale: this.language,
                  },
                )
                : '',
            }),
          )
          return
        }

        if (!this.shouldReloadSlots(selectedDay)) {
          return
        }

        this.isLoadingSlots = true
        this.selectedDay = selectedDay

        const {
          params: { accessToken },
        } = this.$route

        if (!accessToken || typeof accessToken !== 'string' || accessToken.trim().length === 0) {
          this.showError(this.$t('messages.noAccessToken'))
          return
        }

        // Get start and end of the day in client timezone
        let fromDate = null
        // si date selectionnée est la même que minDeliveryPromiseDate sans prendre en compte les heure
        if (
          this.minDeliveryPromiseDate &&
          dateHelper.isSame(
            dateHelper.getDate(this.selectedDay),
            dateHelper.getDate(this.minDeliveryPromiseDate),
            OperationUnit.DAYS,
          )
        ) {
          fromDate = this.minDeliveryPromiseDate
        } else {
          fromDate = selectedDay.split('T')[0] + 'T00:00:00.000Z'
        }

        const toDate = selectedDay.split('T')[0] + 'T23:59:59.999Z'

        const response = await DeliveryService.getSlots(accessToken, {
          fromDate,
          toDate,
        })

        // Update slots with the response data
        if (response?.carrierSlots?.length > 0) {
          const rawSlots = response.carrierSlots[0]?.slots || []
          const mappedSlots = this.mapSlotsToExpectedFormat(rawSlots)
          this.slots = this.sortSlots(mappedSlots)
        } else {
          this.slots = []
        }
      } catch (error) {
        this.slots = []
        this.showError(error)
      } finally {
        this.isLoadingSlots = false
      }
    },

    clearSlots() {
      this.slots = []
      this.selectedDay = null
      this.selectedSlot = null
      this.isDifferentFromOriginal = false
    },

    handleSlotSelected(slot) {
      if (this.selectedDay && slot) {
        const { start, end } = slot
        const combinedSlot = {
          id: `${start}-${end}`,
          start,
          end,
        }

        this.selectedSlot = combinedSlot
      } else {
        this.selectedSlot = slot
      }

      this.isDifferentFromOriginal = this.isSlotDifferent(this.selectedSlot, this.originalSlot)
    },

    isSlotDifferent(newSlot, originalSlot) {
      if (!newSlot || !originalSlot) return false
      return newSlot.start !== originalSlot.start || newSlot.end !== originalSlot.end
    },

    async handleValidateSlot(slot) {
      try {
        if (!this.validateSlotInput(slot)) {
          return
        }

        if (this.isSameAsOriginalSlot(slot)) {
          this.showWarning(this.$t('messages.appointment.sameSlot'))
          return
        }

        const response = await this.validateSlotWithoutBooking(slot)
        const isSuccess = response?.status === 'ok'

        if (isSuccess) {
          const { accessToken } = this.$route.params
          this.showSuccess(this.$t('messages.appointment.slotValidated'))
          setTimeout(() => {
            this.$router.push({
              name: DeliveryClientPageType.APPOINTMENT_CONFIRM,
              params: { accessToken },
              query: { language: this.language },
            })
          }, REDIRECTION_DELAY)
        } else {
          this.showError(this.$t('messages.appointment.slotValidationFailed'))
        }
      } catch (error) {
        this.showError(error)
      }
    },

    validateSlotInput(slot) {
      if (!slot) {
        this.showError(this.$t('messages.appointment.noSlotSelected'))
        return false
      }

      if (!slot.start || !slot.end) {
        this.showError(this.$t('messages.appointment.noSlotSelected'))
        return false
      }

      // Validation des formats de date
      try {
        const startDate = dateHelper.getDate(slot.start)
        const endDate = dateHelper.getDate(slot.end)

        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          this.showError(this.$t('messages.error'))
          return false
        }

        if (startDate >= endDate) {
          this.showError(this.$t('messages.error'))
          return false
        }
      } catch (error) {
        this.showError(this.$t('messages.error'))
        return false
      }

      return true
    },

    isSameAsOriginalSlot(slot) {
      return this.originalSlot && !this.isSlotDifferent(slot, this.originalSlot)
    },

    /**
     * Validates if a date is valid for delivery based on minDeliveryPromiseDate
     * @param {string} selectedDay - The selected day in ISO format
     * @returns {boolean} - True if the date is valid, false otherwise
     */
    isDateValidForDelivery(selectedDay) {
      try {
        const selectedDate = new Date(selectedDay)
        const today = dateHelper.getDate()

        const daysFromToday = differenceInCalendarDays(selectedDate, today)
        if (daysFromToday < 0) {
          return false // Past dates are invalid
        }

        if (!this.minDeliveryPromiseDate) {
          return true
        }

        const minDate = new Date(this.minDeliveryPromiseDate)
        const daysFromMinDate = differenceInCalendarDays(selectedDate, minDate)

        return daysFromMinDate >= 0
      } catch (error) {
        const selectedDate = new Date(selectedDay)
        const today = dateHelper.getDate()
        const daysFromToday = differenceInCalendarDays(selectedDate, today)
        return daysFromToday >= 0
      }
    },

    handleInvalidSlotResponse(response) {
      if (!response) {
        this.showError(this.$t('messages.error'))
        return
      }
      this.showError(this.$t('messages.appointment.slotValidationFailed'))
    },

    showError(error) {
      let message = this.$t('messages.error')
      if (typeof error === 'string') {
        message = error
      } else {
        if (error?.response?.status === 400) {
          message = this.$t('messages.appointment.modificationFailed')
        } else if (error?.response?.status === 403) {
          message = this.$t('messages.appointment.noModificationPermission')
        }
      }
      this.$store.dispatch('setSnackbar', {
        message: message,
        type: SnackBarType.ERROR,
        timeout: MESSAGE_TIMEOUTS.DEFAULT,
      })
    },

    showSuccess(message) {
      this.$store.dispatch('setSnackbar', {
        message: message,
        type: SnackBarType.SUCCESS,
        timeout: MESSAGE_TIMEOUTS.SUCCESS,
      })
    },

    sortSlots(slots) {
      if (!slots || !Array.isArray(slots)) {
        return []
      }
      // Filter out invalid slots
      const validSlots = slots.filter(slot => {
        if (!slot || !slot.start || !slot.end) return false
        if (!this.pickingDates || !this.pickingDates?.start || !this.pickingDates?.end) return true
        const slotStart = new Date(slot.start)
        const slotEnd = new Date(slot.end)
        const pickingStart = new Date(this.pickingDates?.start)
        const pickingEnd = new Date(this.pickingDates?.end)
        return slotStart >= pickingStart && slotEnd >= pickingEnd
      })

      // Create a new array to avoid mutating the original
      const sortedSlots = [...validSlots].sort((a, b) => {
        try {
          const dateA = dateHelper.getDate(a.start)
          const dateB = dateHelper.getDate(b.start)
          if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
            return 0
          }
          return dateA.getTime() - dateB.getTime()
        } catch (error) {
          return 0
        }
      })

      return sortedSlots
    },
    isInitialSlot(slot) {
      if (!slot || !this.originalSlot) return false
      return slot.start === this.originalSlot.start && slot.end === this.originalSlot.end
    },

    mapSlotsToExpectedFormat(slots) {
      if (!slots || !Array.isArray(slots)) {
        return []
      }

      return slots.map(slot => {
        return {
          id: slot.id || `${slot.start}-${slot.end}`,
          start: slot.start,
          end: slot.end,
          formattedSlot: this.getFormattedSlot(slot, false),
          isInitialSlot: this.isInitialSlot(slot),
        }
      })
    },

    // Add method to check if slots need to be reloaded
    shouldReloadSlots(newDay) {
      if (!this.selectedDay || !newDay) return true
      const currentDate = dateHelper.getDate(this.selectedDay)
      const newDate = dateHelper.getDate(newDay)
      return currentDate.toDateString() !== newDate.toDateString()
    },

    async handleMonthChanged({ startDate, endDate }) {
      if (!this.delivery || !this.canUpdate) {
        return
      }

      this.isLoadingUnavailableDates = true
      try {
        const { accessToken } = this.$route.params

        const response = await DeliveryService.getUnavailableDates(accessToken, {
          start: `${startDate}T00:00:00.000Z`,
          end: `${endDate}T23:59:59.999Z`,
        })

        this.unavailableDates = response.unavailableDates || []
      } catch (error) {
        console.error('Failed to load unavailable dates:', error)
        // On error, continue without graying out dates
        this.unavailableDates = []
      } finally {
        this.isLoadingUnavailableDates = false
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.appointment-page {
  background-color: $main-lighten-80-color;
  min-height: 100vh;
}
</style>
